/**
 * Recipes Page
 *
 * Lists recipes with filters, pagination and drawer detail.
 */

import { useState, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Plus } from 'lucide-react';
import { fetchAllForExport, Tabs } from '@bill/ui';
import { PageHeader, Modal, ModalFooter, type SearchAutocompleteOption } from '@/components/ui';
import { Button } from '@/components/ui';
import { useDebounce, useUrlParams, useDrawerRoute, useCanSeeFinancials, usePermission } from '@/hooks';
import { RecipesFilters } from '../components/RecipesFilters';
import { RecipesTable } from '../components/RecipesTable';
import { RecipesFlatView } from '../components/RecipesFlatView';
import { RecipeDetailDrawer } from '../components/RecipeDetailDrawer';
import { CreateRecipeModal } from '../components/CreateRecipeModal';
import { useRecipes, useDuplicateRecipe, useArchiveRecipe, useCalculateRecipeCost, useRecalculateAllCosts } from '../hooks';
import { recipesService } from '../services/recipes.service';
import type { QueryRecipesDto } from '@bstock/shared';

type SortField = QueryRecipesDto['sortBy'];
type SortOrder = QueryRecipesDto['sortOrder'];
type HasProductFilter = 'all' | 'with' | 'without';
type TabId = 'list' | 'flat';

const DEFAULT_LIMIT = 20;

export default function RecipesPage() {
  // Permissions
  const showFinancials = useCanSeeFinancials();
  const canEdit = usePermission('recipes.create');
  const canDelete = usePermission('recipes.delete');

  // Navigation for deep linking
  const navigate = useNavigate();
  const location = useLocation();

  // Modal is open if URL ends with /create
  const isCreateModalOpen = location.pathname.endsWith('/create');

  // Read query params for pre-filling the create modal
  const searchParams = new URLSearchParams(location.search);
  const defaultPosProductId = searchParams.get('posProductId') || undefined;
  const defaultName = searchParams.get('defaultName') || undefined;

  // Local state for autocomplete search inputs (not in URL)
  const [rawProductSearch, setRawProductSearch] = useState('');
  const [posProductSearch, setPosProductSearch] = useState('');

  // Archive confirmation state
  const [archiveConfirmId, setArchiveConfirmId] = useState<string | null>(null);

  // Local state for selected autocomplete options (to display labels)
  const [selectedRawProduct, setSelectedRawProduct] = useState<SearchAutocompleteOption | null>(null);
  const [selectedPosProduct, setSelectedPosProduct] = useState<SearchAutocompleteOption | null>(null);

  // URL-synced filters
  // `tab` est dans l'URL pour permettre le deep linking vers la vue a plat.
  const { params, setParam, setParams, resetParams } = useUrlParams({
    tab: { default: 'list' as TabId },
    search: { default: '' },
    hasProduct: { default: 'all' as HasProductFilter },
    rawProductId: { default: '' },
    posProductId: { default: '' },
    page: { default: 1, type: 'number' },
    limit: { default: DEFAULT_LIMIT, type: 'number' },
    sortBy: { default: 'name' as SortField },
    sortOrder: { default: 'asc' as SortOrder },
  });

  const activeTab = (params.tab as TabId) ?? 'list';

  // URL-synced drawer state
  const drawer = useDrawerRoute('/recettes', { paramName: 'recipeId' });

  // Debounce search
  const debouncedSearch = useDebounce(params.search, 300);

  // Parse hasProduct filter
  const hasProductValue = params.hasProduct === 'all'
    ? undefined
    : params.hasProduct === 'with'
      ? true
      : false;

  // Data fetching
  const { data, isLoading, error, isPlaceholderData } = useRecipes({
    page: params.page,
    limit: params.limit,
    search: debouncedSearch || undefined,
    hasProduct: hasProductValue,
    rawProductId: params.rawProductId || undefined,
    posProductId: params.posProductId || undefined,
    sortBy: params.sortBy as SortField,
    sortOrder: params.sortOrder as SortOrder,
  });

  // Find recipe to archive for confirmation modal
  const recipeToArchive = data?.data.find(r => r.id === archiveConfirmId);

  // Mutations
  const duplicateRecipe = useDuplicateRecipe();
  const archiveRecipe = useArchiveRecipe();
  const calculateCost = useCalculateRecipeCost();
  const recalculateAllCosts = useRecalculateAllCosts();

  // Check if filters are active
  const hasActiveFilters = Boolean(
    params.search ||
    params.hasProduct !== 'all' ||
    params.rawProductId ||
    params.posProductId
  );

  // Handlers
  const handleSearchChange = useCallback((value: string) => {
    setParams({ search: value, page: 1 });
  }, [setParams]);

  const handleHasProductChange = useCallback((value: HasProductFilter) => {
    setParams({ hasProduct: value, page: 1 });
  }, [setParams]);

  const handleRawProductChange = useCallback((option: SearchAutocompleteOption | null) => {
    setSelectedRawProduct(option);
    setParams({ rawProductId: option?.id || '', page: 1 });
  }, [setParams]);

  const handlePosProductChange = useCallback((option: SearchAutocompleteOption | null) => {
    setSelectedPosProduct(option);
    setParams({ posProductId: option?.id || '', page: 1 });
  }, [setParams]);

  const handlePageChange = useCallback((page: number) => {
    setParam('page', page);
  }, [setParam]);

  const handleLimitChange = useCallback((limit: number) => {
    setParams({ limit, page: 1 });
  }, [setParams]);

  const handleSort = useCallback((field: SortField, order: SortOrder) => {
    setParams({ sortBy: field, sortOrder: order, page: 1 });
  }, [setParams]);

  const handleReset = useCallback(() => {
    setSelectedRawProduct(null);
    setSelectedPosProduct(null);
    setRawProductSearch('');
    setPosProductSearch('');
    resetParams();
  }, [resetParams]);

  const handleRowClick = useCallback((recipeId: string) => {
    drawer.open(recipeId);
  }, [drawer]);

  const handleDrawerClose = useCallback((open: boolean) => {
    if (!open) drawer.close();
  }, [drawer]);

  const handleCreateClick = useCallback(() => {
    navigate('/recettes/create');
  }, [navigate]);

  const handleCreateModalClose = useCallback(() => {
    navigate('/recettes');
  }, [navigate]);

  const handleCreateSuccess = useCallback((recipeId: string) => {
    navigate('/recettes'); // Close the modal
    drawer.open(recipeId); // Open drawer with the new recipe
  }, [navigate, drawer]);

  const handleDuplicate = useCallback((recipeId: string) => {
    duplicateRecipe.mutate(
      { id: recipeId },
      {
        onSuccess: (recipe) => {
          drawer.open(recipe.id);
        },
      }
    );
  }, [duplicateRecipe, drawer]);

  const handleArchive = useCallback((recipeId: string) => {
    setArchiveConfirmId(recipeId);
  }, []);

  const handleConfirmArchive = useCallback(() => {
    if (!archiveConfirmId) return;
    archiveRecipe.mutate(archiveConfirmId, {
      onSuccess: () => setArchiveConfirmId(null),
    });
  }, [archiveConfirmId, archiveRecipe]);

  const handleCalculateCost = useCallback((recipeId: string) => {
    calculateCost.mutate(recipeId);
  }, [calculateCost]);

  const handleRecalculateAllCosts = useCallback(() => {
    recalculateAllCosts.mutate();
  }, [recalculateAllCosts]);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <PageHeader
          title="Recettes"
          subtitle="Fiches techniques et couts de production"
        />
        {canEdit && (
          <div className="flex items-center gap-3">
            <Button variant="primary" onClick={handleCreateClick}>
              <Plus className="w-4 h-4" />
              Creer une recette
            </Button>
          </div>
        )}
      </div>

      {/* Onglets : liste paginee vs vue a plat exportable */}
      <Tabs
        tabs={[
          { id: 'list', label: 'Recettes' },
          { id: 'flat', label: 'Donnees a plat' },
        ]}
        activeTab={activeTab}
        onTabChange={(id) => setParam('tab', id as TabId)}
      />

      {activeTab === 'flat' ? (
        <RecipesFlatView enabled={activeTab === 'flat'} />
      ) : (
      <>
      {/* Filters */}
      <RecipesFilters
        search={params.search}
        onSearchChange={handleSearchChange}
        hasProduct={params.hasProduct as HasProductFilter}
        onHasProductChange={handleHasProductChange}
        rawProductSearch={rawProductSearch}
        onRawProductSearchChange={setRawProductSearch}
        selectedRawProduct={selectedRawProduct}
        onRawProductChange={handleRawProductChange}
        posProductSearch={posProductSearch}
        onPosProductSearchChange={setPosProductSearch}
        selectedPosProduct={selectedPosProduct}
        onPosProductChange={handlePosProductChange}
        onReset={handleReset}
        hasActiveFilters={hasActiveFilters}
        onRecalculateAllCosts={handleRecalculateAllCosts}
        isRecalculating={recalculateAllCosts.isPending}
        showFinancials={showFinancials}
      />

      {/* Table */}
      <RecipesTable
        recipes={data?.data ?? []}
        pagination={data?.pagination ?? { page: 1, limit: params.limit, total: 0, totalPages: 0 }}
        isLoading={isLoading || isPlaceholderData}
        error={error}
        onRowClick={handleRowClick}
        onPageChange={handlePageChange}
        onLimitChange={handleLimitChange}
        sortBy={(params.sortBy || 'name') as NonNullable<SortField>}
        sortOrder={(params.sortOrder || 'asc') as NonNullable<SortOrder>}
        onSort={handleSort}
        onDuplicate={handleDuplicate}
        onArchive={handleArchive}
        onCalculateCost={handleCalculateCost}
        hasFilters={hasActiveFilters}
        onCreateClick={handleCreateClick}
        onFetchAllRows={() =>
          fetchAllForExport(async ({ limit: exportLimit }) => {
            const response = await recipesService.list({
              page: 1,
              limit: exportLimit,
              search: debouncedSearch || undefined,
              hasProduct: hasProductValue,
              rawProductId: params.rawProductId || undefined,
              posProductId: params.posProductId || undefined,
              sortBy: params.sortBy as SortField,
              sortOrder: params.sortOrder as SortOrder,
            });
            return {
              items: response.data,
              pagination: { total: response.pagination.total },
            };
          })
        }
        showFinancials={showFinancials}
      />
      </>
      )}

      {/* Drawer */}
      <RecipeDetailDrawer
        recipeId={drawer.resourceId}
        open={drawer.isOpen}
        onOpenChange={handleDrawerClose}
        showFinancials={showFinancials}
        canEdit={canEdit}
        canDelete={canDelete}
      />

      {/* Create Modal */}
      <CreateRecipeModal
        isOpen={isCreateModalOpen}
        onClose={handleCreateModalClose}
        onSuccess={handleCreateSuccess}
        defaultPosProductId={defaultPosProductId}
        defaultName={defaultName}
      />

      {/* Archive Confirmation Modal */}
      <Modal
        isOpen={!!archiveConfirmId}
        onClose={() => setArchiveConfirmId(null)}
        title="Archiver la recette"
        size="sm"
      >
        <p className="text-sm text-neutral-600 mb-6">
          Etes-vous sur de vouloir archiver "{recipeToArchive?.name}" ?
          <span className="block mt-2 text-neutral-500">
            Cette action est reversible.
          </span>
        </p>
        <ModalFooter>
          <Button variant="ghost" onClick={() => setArchiveConfirmId(null)}>
            Annuler
          </Button>
          <Button
            variant="danger"
            onClick={handleConfirmArchive}
            isLoading={archiveRecipe.isPending}
          >
            Archiver
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
}
